import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MUITextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import MUIButton from '@mui/material/Button';
import React, { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useLoading, useSnackbar } from 'contexts';
import { RegistrationSchema } from 'schemas/register';
import { signUp } from 'services';
import Session from 'utils/Session';

const PrivacyPolicy = "https://assets.languify.in/api-docs/Languify-Privacy-Policy.pdf";

const InitialValues = { name: '', email: '', password: '' };

const FEATURES = [
  'Daily practice with real-time feedback',
  'Full-length mock exams (Academic & General)',
  'Native-level vocabulary & grammar analysis',
];

// ─── Register Header ─────────────────────────────────────────────────────────

function RegisterHeader() {
  return (
    <Box
      sx={(theme) => ({
        width: '100%',
        backgroundColor: theme.palette.shades['clr-white-900'],
        borderBottom: `1px solid ${theme.palette.neutral['clr-200']}`,
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 40px',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1100,
        [theme.breakpoints.down('md')]: {
          padding: '0 16px',
        },
      })}
    >
      {/* Logo */}
      <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            backgroundColor: '#0284C5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography component="span" variant="body01-bold" sx={{ color: '#FFFFFF', fontSize: '14px' }}>
            IB
          </Typography>
        </Box>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
            <Typography component="span" variant="body01-bold" sx={{ color: '#0284C5' }}>
              IELTS
            </Typography>
            &nbsp;
            <Typography component="span" variant="body01-bold" color="neutral.clr-900">
              BandBoost
            </Typography>
          </Box>
          <Typography
            component="div"
            variant="overline-semiBold"
            sx={{ color: '#0284C5', fontSize: '9px', lineHeight: 1, letterSpacing: '0.08em' }}
          >
            BY LANGUIFY
          </Typography>
        </Box>
      </Link>

    </Box>
  );
}

// ─── Shared left marketing panel ─────────────────────────────────────────────

function LeftPanel() {
  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        px: '64px',
        py: '48px',
      }}
    >
      {/* Badge */}
      <Box
        sx={(theme) => ({
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          border: `1px solid ${theme.palette.secondary['clr-200']}`,
          backgroundColor: theme.palette.secondary['clr-50'],
          borderRadius: '50px',
          padding: '5px 14px',
          mb: '28px',
          width: 'fit-content',
        })}
      >
        <AutoAwesomeIcon sx={{ color: '#0284C5', fontSize: '0.8rem' }} />
        <Typography component="span" variant="overline-bold" sx={{ color: '#0284C5' }}>
          AI-Powered Preparation
        </Typography>
      </Box>

      {/* Heading */}
      <Typography
        component="h1"
        variant="h2-bold"
        color="neutral.clr-900"
        sx={{ mb: '20px' }}
      >
        Elevate your IELTS{' '}
        <br />
        score with{' '}
        <Typography component="span" variant="h2-bold" sx={{ color: '#0284C5' }}>
          BandBoost
        </Typography>
      </Typography>

      {/* Subtext */}
      <Typography
        component="p"
        variant="body01-regular"
        color="neutral.clr-500"
        sx={{ lineHeight: 1.7, mb: '32px', maxWidth: '380px' }}
      >
        Join thousands of students achieving Band 8.0+ with our personalized
        AI coaching and academic assessment platform.
      </Typography>

      {/* Feature list */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {FEATURES.map((f) => (
          <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <CheckCircleOutlineIcon sx={{ color: '#0284C5', fontSize: '1.1rem', flexShrink: 0 }} />
            <Typography component="span" variant="body02-medium" color="neutral.clr-700">
              {f}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ─── Shared input style ──────────────────────────────────────────────────────

const inputSx = (theme) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: theme.palette.shades['clr-white-900'],
    ...theme.typography['body02-regular'],
    '& fieldset': { borderColor: theme.palette.neutral['clr-200'] },
    '&:hover fieldset': { borderColor: theme.palette.neutral['clr-300'] },
    '&.Mui-focused fieldset': { borderColor: '#0284C5', borderWidth: '1.5px' },
    '&.Mui-error fieldset': { borderColor: theme.palette.danger?.main || '#E2483C' },
  },
  '& input': {
    padding: '11px 14px 11px 0',
    color: theme.palette.neutral['clr-900'],
    ...theme.typography['body02-regular'],
    '&::placeholder': { color: theme.palette.neutral['clr-400'], opacity: 1 },
  },
});

// ─── Form field component ────────────────────────────────────────────────────

function FormField({ label, placeholder, icon: Icon, type = 'text', name, value, onChange, error, inputProps, endAdornment }) {
  return (
    <Box sx={{ width: '100%', mb: '20px' }}>
      <Typography
        component="label"
        variant="body02-semiBold"
        color="neutral.clr-700"
        sx={{ display: 'block', mb: '8px' }}
      >
        {label}
      </Typography>
      <MUITextField
        fullWidth
        type={type}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        error={!!error}
        inputProps={inputProps}
        InputProps={{
          startAdornment: Icon ? (
            <InputAdornment position="start">
              <Icon sx={{ color: 'neutral.clr-400', fontSize: '1.1rem' }} />
            </InputAdornment>
          ) : undefined,
          endAdornment: endAdornment || undefined,
        }}
        sx={inputSx}
      />
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function Register() {
  const loading = useLoading();
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const { type } = useParams();
  const [queryParams] = useSearchParams();

  const [agreeTerms, setAgreeTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [values, setValues] = useState(InitialValues);
  const [errors, setErrors] = useState({});

  const handleRegister = async (e) => {
    try {
      e.preventDefault();
      setErrors({});

      const formData = new FormData(e.target);
      const vals = {};
      formData.forEach((value, key) => { vals[key] = value; });

      const { error, value } = RegistrationSchema.validate(vals, { abortEarly: false });

      if (error) {
        setErrors(error.details.reduce((acc, curr) => {
          acc[curr.path.toString()] = true;
          return acc;
        }, {}));
        return;
      }

      if (!agreeTerms) {
        snackbar.error("Please agree to the Terms of Service and Privacy Policy to continue.");
        return;
      }

      loading.show();
      const data = await signUp({ name: value.name, email: value.email, password: value.password });

      snackbar.success("Account created successfully!");
      if (data?.user?.metadata?.educationInfo || !type) navigate('/practice');
      else navigate(`/${type}/education-info`);
    } catch (error) {
      console.error(error);
      snackbar.error(error.message || "Uh Oh! Something went wrong!");
    } finally {
      loading.hide();
    }
  };

  React.useEffect(() => {
    if (Session.isLoggedIn()) navigate("/practice");
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <RegisterHeader />

      <Box
        sx={(theme) => ({
          flex: 1,
          backgroundColor: theme.palette.shades['clr-offWhite'],
          display: 'flex',
          flexDirection: 'row',
          pt: '60px',
          maxWidth: '1280px',
          margin: '0 auto',
          width: '100%',
          [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
          },
        })}
      >
        {/* Left panel */}
        <LeftPanel />

        {/* Right panel */}
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: '40px',
            py: '48px',
          }}
        >
          <Box
              component="form"
              onSubmit={handleRegister}
              sx={(theme) => ({
                width: '418px',
                backgroundColor: theme.palette.shades['clr-white-900'],
                borderRadius: '16px',
                border: `1px solid ${theme.palette.neutral['clr-200']}`,
                padding: '40px',
              })}
            >
              <Typography component="h1" variant="h4-bold" color="neutral.clr-900" sx={{ mb: '8px' }}>
                Create your account
              </Typography>
              <Typography component="p" variant="body02-regular" color="neutral.clr-500" sx={{ mb: '28px' }}>
                Start your journey to success today.
              </Typography>

              {/* Full Name */}
              <FormField
                label="Full Name"
                placeholder="John Doe"
                icon={PersonOutlinedIcon}
                name="name"
                value={values.name}
                error={errors.name}
                onChange={(e) => setValues((v) => ({ ...v, name: e.target.value }))}
                inputProps={{ autoComplete: 'name' }}
              />

              {/* Email */}
              <FormField
                label="Email Address"
                placeholder="name@example.com"
                icon={EmailOutlinedIcon}
                type="email"
                name="email"
                value={values.email}
                error={errors.email}
                onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))}
                inputProps={{ autoComplete: 'email' }}
              />

              {/* Password */}
              <Box sx={{ width: '100%', mb: '4px' }}>
                <Typography
                  component="label"
                  variant="body02-semiBold"
                  color="neutral.clr-700"
                  sx={{ display: 'block', mb: '8px' }}
                >
                  Password
                </Typography>
                <MUITextField
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="••••••••"
                  value={values.password}
                  error={!!errors.password}
                  onChange={(e) => setValues((v) => ({ ...v, password: e.target.value }))}
                  inputProps={{ autoComplete: 'new-password' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon sx={{ color: 'neutral.clr-400', fontSize: '1.1rem' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                          sx={{ color: 'neutral.clr-400' }}
                        >
                          {showPassword
                            ? <VisibilityOffOutlinedIcon sx={{ fontSize: '1.1rem' }} />
                            : <VisibilityOutlinedIcon sx={{ fontSize: '1.1rem' }} />
                          }
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={(theme) => ({
                    ...inputSx(theme),
                    '& input': {
                      padding: '11px 0',
                      color: theme.palette.neutral['clr-900'],
                      ...theme.typography['body02-regular'],
                      '&::placeholder': { color: theme.palette.neutral['clr-400'], opacity: 1 },
                    },
                  })}
                />
              </Box>
              <Typography
                component="p"
                variant="overline-semiBold"
                color="neutral.clr-400"
                sx={{ mb: '20px', mt: '6px' }}
              >
                Min. 8 characters with numbers
              </Typography>

              {/* Terms checkbox */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '24px' }}>
                <Checkbox
                  size="small"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  sx={(theme) => ({
                    padding: 0,
                    color: theme.palette.neutral['clr-300'],
                    '&.Mui-checked': { color: '#0284C5' },
                  })}
                />
                <Typography component="span" variant="body02-regular" color="neutral.clr-500">
                  I agree to the{' '}
                  <Typography
                    component="a"
                    href={PrivacyPolicy}
                    target="_blank"
                    rel="noreferrer"
                    variant="body02-regular"
                    sx={{ color: '#0284C5', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    Terms of Service
                  </Typography>
                  {' '}and{' '}
                  <Typography
                    component="a"
                    href={PrivacyPolicy}
                    target="_blank"
                    rel="noreferrer"
                    variant="body02-regular"
                    sx={{ color: '#0284C5', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    Privacy Policy
                  </Typography>
                  .
                </Typography>
              </Box>

              {/* Create Account button */}
              <MUIButton
                type="submit"
                fullWidth
                variant="contained"
                disableElevation
                sx={(theme) => ({
                  backgroundColor: '#0284C5',
                  color: theme.palette.shades['clr-white-900'],
                  borderRadius: '8px',
                  padding: '13px',
                  textTransform: 'none',
                  ...theme.typography['body01-bold'],
                  mb: '24px',
                  '&:hover': { backgroundColor: '#026BA1' },
                })}
              >
                Create Account &nbsp;&rarr;
              </MUIButton>

              {/* Sign in link */}
              <Typography
                component="p"
                variant="body02-regular"
                color="neutral.clr-500"
                sx={{ textAlign: 'center' }}
              >
                Already have an account?{' '}
                <Link
                  to={`/login?aid=${queryParams.get("aid")}&rinfo=true`}
                  style={{ color: '#0284C5', fontWeight: 600, textDecoration: 'none' }}
                >
                  Sign In
                </Link>
              </Typography>
            </Box>
        </Box>
      </Box>

    </Box>
  );
}

export default Register;
